import { useTranslation } from "react-i18next"
import { useAppSelector } from "../reducers/hooks"
import { Chat, Participant } from "../reducers/chatsSlice"
import { findProfile } from "../reducers/profilesSliceSelectors"
import { participantPhotoToSrc } from "../components/AvatarImage"
import { selectAccountChatByJid } from "../reducers/accountChatsSlice"
import { selectCarById } from "../reducers/carsSlice"
import { selectEffectiveDriverForChat } from "../reducers/driverResolutionSelectors"
import { isDriverRoomGroup } from "../lib/slickAISilence"
import { withoutCrewNames } from "../lib/chatHeaderTitle"

type ChatInfoReturn = {
  driverParticipant: Participant | undefined
  chatTitle: string
  photoSrc: string | undefined
  additionalInfo: string
  /** The vehicle alone, for the agent header that already names the crew. */
  vehicleLabel: string | undefined
  driverPhone: string
  driverBPhone: string
  driverAName: string
  driverBName: string
}

const useChatInfo = (chat: Chat): ChatInfoReturn => {
  const { t } = useTranslation()
  const effectiveDriver = useAppSelector(selectEffectiveDriverForChat(chat.jid))
  const profile = useAppSelector(findProfile(chat.jid))
  const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
  const driverBProfile = useAppSelector(findProfile(accountChat?.driverB))
  const car = useAppSelector(selectCarById(accountChat?.carId))

  const isDoubleCrew = Boolean(accountChat?.driverA && accountChat?.driverB)
  const isDriverRoom = isDriverRoomGroup(accountChat?.group)
  const missingVehicleAndDriver =
    isDriverRoom &&
    !accountChat?.carId &&
    !accountChat?.driverA &&
    !accountChat?.driverB

  const vehicleLabel = (): string | undefined => {
    // For chats with a car, return car label
    if (accountChat?.carId && car?.label) {
      return car.label
    }

    // For double crew chats without car, return accountChat name as registration
    if (accountChat?.driverA && accountChat.name) {
      return accountChat.name
    }

    // Only a driver room is named after a vehicle and a crew, so only it can be
    // missing both. A cmr, car issues, Slick AI or any other channel never
    // seats a crew — the warning there is noise.
    if (missingVehicleAndDriver) {
      return undefined
    }

    if (!effectiveDriver) return undefined
    const plates =
      "plates" in effectiveDriver ? effectiveDriver.plates : undefined
    const attrs = [plates].filter((attr) => attr)

    if (attrs.length === 0) return undefined

    return attrs[0] || undefined
  }

  const resolvedVehicleLabel = vehicleLabel()

  const chatDriverPhone = () => {
    if (
      effectiveDriver &&
      "phone" in effectiveDriver &&
      effectiveDriver.phone
    ) {
      return effectiveDriver.phone
    }
    return ""
  }

  const chatDriverBPhone = () => {
    if (
      isDoubleCrew &&
      driverBProfile &&
      "phone" in driverBProfile &&
      driverBProfile.phone
    ) {
      return driverBProfile.phone
    }
    return ""
  }

  const chatTitle = () => {
    if (isDoubleCrew && effectiveDriver && driverBProfile) {
      const driverAName =
        "firstName" in effectiveDriver &&
        effectiveDriver.firstName &&
        effectiveDriver.lastName
          ? `${effectiveDriver.firstName} ${effectiveDriver.lastName}`
          : effectiveDriver.name
      const driverBName =
        "firstName" in driverBProfile &&
        driverBProfile.firstName &&
        driverBProfile.lastName
          ? `${driverBProfile.firstName} ${driverBProfile.lastName}`
          : driverBProfile.name
      return `${driverAName} & ${driverBName}`
    }
    if (effectiveDriver) {
      if (
        "firstName" in effectiveDriver &&
        effectiveDriver.firstName &&
        effectiveDriver.lastName
      ) {
        return `${effectiveDriver.firstName} ${effectiveDriver.lastName}`
      }
      return effectiveDriver.name
    } else if (chat.room) {
      return chat.name
    } else if (profile) {
      return profile.name
    } else {
      return chat.name
    }
  }

  const photoSrc = () => {
    return participantPhotoToSrc(
      effectiveDriver ? effectiveDriver.photo : profile?.photo,
    )
  }

  const driverAName = () => {
    if (!effectiveDriver) return ""
    if (
      "firstName" in effectiveDriver &&
      effectiveDriver.firstName &&
      effectiveDriver.lastName
    ) {
      return `${effectiveDriver.firstName} ${effectiveDriver.lastName}`
    }
    return effectiveDriver.name
  }

  const driverBName = () => {
    if (!isDoubleCrew || !driverBProfile) return ""
    if (
      "firstName" in driverBProfile &&
      driverBProfile.firstName &&
      driverBProfile.lastName
    ) {
      return `${driverBProfile.firstName} ${driverBProfile.lastName}`
    }
    return driverBProfile.name
  }

  return {
    driverParticipant: effectiveDriver as Participant | undefined,
    chatTitle: chatTitle(),
    photoSrc: photoSrc(),
    additionalInfo: missingVehicleAndDriver
      ? t("chat.chatInfo.noVehicleAndDriver")
      : (resolvedVehicleLabel ?? ""),
    vehicleLabel:
      resolvedVehicleLabel &&
      withoutCrewNames(resolvedVehicleLabel, [driverAName(), driverBName()]),
    driverPhone: chatDriverPhone(),
    driverBPhone: chatDriverBPhone(),
    driverAName: driverAName(),
    driverBName: driverBName(),
  }
}

export default useChatInfo
